import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Collapse, Tag, Typography } from 'antd';
import config from 'config';
import { useGetLocationsQuery, useGetRegionsQuery } from 'gql/types';
import { EditUserInfoSchema } from 'modules/admin/components/EditUserForm/schema';
import { FormItem, Input, Spin } from 'modules/common/components';
import { useRegionLocationSync } from 'modules/locations/hooks/useRegionLocationSync';
import { FC, useCallback, useMemo, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

const { Panel } = Collapse;
const { Text } = Typography;

type LocationItem = {
  id: string;
  name: string;
};

type RegionGroup = {
  regionName: string;
  locations: LocationItem[];
};

type Props = {
  control: Control<EditUserInfoSchema>;
  setValue: UseFormSetValue<EditUserInfoSchema>;
  disabled?: boolean;
};

const AssignedLocationsSelect: FC<Props> = ({
  control,
  setValue,
  disabled,
}) => {
  const { t } = useTranslation('common.AssignedLocationsSelect');

  const [searchValue, setSearchValue] = useState('');

  const assignedRegionIds =
    useWatch({ control, name: 'assignedRegionIds' }) ?? [];

  const assignedLocationIds =
    useWatch({ control, name: 'assignedLocationIds' }) ?? [];

  const hasRegions = assignedRegionIds.length > 0;

  const { data: regionsData, loading: regionsLoading } = useGetRegionsQuery();
  const regions = regionsData?.getRegions || [];

  const regionNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    regions.forEach((region) => {
      map.set(region.id, region.name);
    });
    return map;
  }, [regionsData]);

  const { data: locationsData, loading: locationsLoading } =
    useGetLocationsQuery({
      variables: {
        input: {
          skip: 0,
          take: 1000,
          filter: hasRegions ? { regionIds: assignedRegionIds } : undefined,
        },
      },
      skip: !hasRegions,
      fetchPolicy: 'cache-and-network',
    });

  const locationsByRegion = useMemo(() => {
    const locations = locationsData?.getLocations.data ?? [];
    const grouped = new Map<string, RegionGroup>();

    locations.forEach((location) => {
      const regionId = location.region?.id;
      const regionName = location.region?.name ?? 'Unknown';

      if (!regionId) {
        return;
      }

      if (!grouped.has(regionId)) {
        grouped.set(regionId, { regionName, locations: [] });
      }

      grouped.get(regionId)!.locations.push({
        id: location.id,
        name: location.name,
      });
    });

    return grouped;
  }, [locationsData]);

  const allAvailableLocationIds = useMemo(() => {
    return (locationsData?.getLocations.data ?? []).map((l) => l.id);
  }, [locationsData]);

  const totalLocations = allAvailableLocationIds.length;

  const filteredLocationsByRegion = useMemo(() => {
    if (!searchValue.trim()) {
      return locationsByRegion;
    }

    const query = searchValue.toLowerCase().trim();
    const filtered = new Map<string, RegionGroup>();

    locationsByRegion.forEach((group, regionId) => {
      const matchedLocations = group.locations.filter((loc) =>
        loc.name.toLowerCase().includes(query)
      );

      if (matchedLocations.length > 0) {
        filtered.set(regionId, {
          regionName: group.regionName,
          locations: matchedLocations,
        });
      }
    });

    return filtered;
  }, [locationsByRegion, searchValue]);

  const emptyRegionIds = useMemo(() => {
    const regionsWithLocations = new Set(locationsByRegion.keys());
    return assignedRegionIds.filter((id) => !regionsWithLocations.has(id));
  }, [assignedRegionIds, locationsByRegion]);

  const setLocations = useCallback(
    (locationIds: string[]) => {
      setValue('assignedLocationIds', locationIds, { shouldDirty: true });
    },
    [setValue]
  );

  const defaultActiveKeys = useMemo(() => {
    if (totalLocations > config.UI.MAX_ASSIGNED_LOCATIONS_TO_CLOSE_SECTION) {
      return [];
    }
    return Array.from(filteredLocationsByRegion.keys());
  }, [filteredLocationsByRegion, totalLocations]);

  useRegionLocationSync({
    availableLocationsIds: allAvailableLocationIds,
    assignedLocationIds,
    isLoading: regionsLoading || locationsLoading,
    hasData: !!regionsData && !!locationsData,
    onChange: setLocations,
  });

  const selectedIds = new Set(assignedLocationIds);

  const handleToggleLocation = (locationId: string, checked: boolean) => {
    if (disabled) {
      return;
    }

    const updated = checked
      ? [...assignedLocationIds, locationId]
      : assignedLocationIds.filter((id) => id !== locationId);

    setLocations(updated);
  };

  const handleToggleRegion = (regionId: string, checked: boolean) => {
    if (disabled) {
      return;
    }

    const regionLocations = locationsByRegion.get(regionId)?.locations ?? [];
    const regionLocationIds = regionLocations.map((l) => l.id);

    let updated: string[];
    if (checked) {
      const currentSet = new Set(assignedLocationIds);
      const newIds = regionLocationIds.filter((id) => !currentSet.has(id));
      updated = [...assignedLocationIds, ...newIds];
    } else {
      const removeSet = new Set(regionLocationIds);
      updated = assignedLocationIds.filter((id) => !removeSet.has(id));
    }

    setLocations(updated);
  };

  if (!hasRegions) {
    return (
      <FormItem label={t('assignedLocations')}>
        <div className={styles.placeholder}>{t('selectRegionFirst')}</div>
      </FormItem>
    );
  }

  if (locationsLoading) {
    return (
      <FormItem label={t('assignedLocations')}>
        <div className={styles.loadingContainer}>
          <Spin size="small" />
        </div>
      </FormItem>
    );
  }

  return (
    <FormItem label={t('assignedLocations')}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.summary}>
            {t('selectedCount', {
              selected: selectedIds.size,
              total: allAvailableLocationIds.length,
            })}
          </div>
          <Input
            className={styles.searchInput}
            placeholder={t('searchLocations')}
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            size="small"
            disabled={disabled}
          />
        </div>

        <div className={styles.accordionWrapper}>
          {filteredLocationsByRegion.size === 0 && searchValue ? (
            <div className={styles.noResults}>
              <Text type="secondary">{t('noLocationsFound')}</Text>
            </div>
          ) : (
            <Collapse
              defaultActiveKey={defaultActiveKeys}
              className={styles.accordion}
            >
              {Array.from(filteredLocationsByRegion.entries()).map(
                ([regionId, { regionName, locations }]) => {
                  const allRegionLocations =
                    locationsByRegion.get(regionId)?.locations ?? [];
                  const allRegionLocationIds = allRegionLocations.map(
                    (l) => l.id
                  );
                  const selectedInRegion = allRegionLocationIds.filter((id) =>
                    selectedIds.has(id)
                  );
                  const allSelected =
                    selectedInRegion.length === allRegionLocationIds.length;
                  const someSelected =
                    selectedInRegion.length > 0 && !allSelected;

                  return (
                    <Panel
                      key={regionId}
                      header={
                        <div className={styles.regionHeader}>
                          <div
                            className={styles.regionCheckbox}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              disabled={disabled}
                              onChange={(e) =>
                                handleToggleRegion(regionId, e.target.checked)
                              }
                            >
                              {regionName}
                            </Checkbox>
                          </div>
                          <Tag>
                            {selectedInRegion.length}/
                            {allRegionLocationIds.length}
                          </Tag>
                        </div>
                      }
                    >
                      <div className={styles.locationsGrid}>
                        {locations.map((location) => (
                          <Checkbox
                            key={location.id}
                            checked={selectedIds.has(location.id)}
                            disabled={disabled}
                            onChange={(e) =>
                              handleToggleLocation(
                                location.id,
                                e.target.checked
                              )
                            }
                          >
                            {location.name}
                          </Checkbox>
                        ))}
                      </div>
                    </Panel>
                  );
                }
              )}

              {!searchValue &&
                emptyRegionIds.map((regionId) => (
                  <Panel
                    key={regionId}
                    header={
                      <div className={styles.regionHeader}>
                        <span>{regionNamesMap.get(regionId) ?? regionId}</span>
                        <Tag>0/0</Tag>
                      </div>
                    }
                  >
                    <Text
                      type="secondary"
                      italic
                      className={styles.emptyRegion}
                    >
                      {t('noLocationsAvailable')}
                    </Text>
                  </Panel>
                ))}
            </Collapse>
          )}
        </div>
      </div>
    </FormItem>
  );
};

export default AssignedLocationsSelect;
